// services/sessionSync.ts

/**
 * Cross-tab session events (ORA-987). The session is one httpOnly cookie
 * shared by every tab, so a sign-in, sign-out or impersonation switch in one
 * tab changes whose session the others are using. Each change is announced
 * on a `BroadcastChannel`; a browser without one gets the same event through
 * a `storage` event on a dedicated localStorage key.
 *
 * This module only carries events; `services/auth` decides what a receiving
 * tab does with them.
 */

export const SESSION_SYNC_CHANNEL = 'ora-session';
export const SESSION_SYNC_STORAGE_KEY = 'ora-session-event';

export type SessionChangeType =
  | 'signed-in'
  | 'signed-out'
  | 'impersonation-started'
  | 'impersonation-stopped';

export interface SessionChangeEvent {
  type: SessionChangeType;
  /** The account the session now belongs to; null after a sign-out. */
  userId: string | null;
  /** The tab that sent it (a tab never handles its own events). */
  sourceTabId: string;
  /** Makes every storage write a change, so the event always fires. */
  sentAt: number;
}

const SESSION_CHANGE_TYPES: ReadonlySet<string> = new Set<SessionChangeType>([
  'signed-in',
  'signed-out',
  'impersonation-started',
  'impersonation-stopped',
]);

const tabId = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2)}`;

let channel: BroadcastChannel | null | undefined;

const getChannel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;
  try {
    channel =
      typeof BroadcastChannel === 'function'
        ? new BroadcastChannel(SESSION_SYNC_CHANNEL)
        : null;
  } catch {
    channel = null;
  }
  return channel;
};

const readEvent = (value: unknown): SessionChangeEvent | null => {
  if (!value || typeof value !== 'object') return null;
  const event = value as Partial<SessionChangeEvent>;
  if (
    typeof event.type !== 'string' ||
    !SESSION_CHANGE_TYPES.has(event.type) ||
    typeof event.sourceTabId !== 'string' ||
    (event.userId !== null && typeof event.userId !== 'string')
  ) {
    return null;
  }
  return event as SessionChangeEvent;
};

/** Tells the other tabs that this tab's session changed. */
export const broadcastSessionChange = (
  type: SessionChangeType,
  userId: string | null
): void => {
  const event: SessionChangeEvent = {
    type,
    userId,
    sourceTabId: tabId,
    sentAt: Date.now(),
  };

  const sessionChannel = getChannel();
  if (sessionChannel) {
    try {
      sessionChannel.postMessage(event);
      return;
    } catch {
      // Fall through to the storage event.
    }
  }

  try {
    localStorage.setItem(SESSION_SYNC_STORAGE_KEY, JSON.stringify(event));
  } catch {
    // No way to reach the other tabs; each finds out on its next request.
  }
};

/**
 * Calls `onChange` for every session event another tab sends. Returns the
 * unsubscribe.
 */
export const subscribeToSessionChanges = (
  onChange: (event: SessionChangeEvent) => void
): (() => void) => {
  const deliver = (value: unknown) => {
    const event = readEvent(value);
    if (event && event.sourceTabId !== tabId) onChange(event);
  };

  const sessionChannel = getChannel();
  if (sessionChannel) {
    const onMessage = (message: MessageEvent) => deliver(message.data);
    sessionChannel.addEventListener('message', onMessage);
    return () => sessionChannel.removeEventListener('message', onMessage);
  }

  const onStorage = (storageEvent: StorageEvent) => {
    if (storageEvent.key !== SESSION_SYNC_STORAGE_KEY) return;
    if (!storageEvent.newValue) return;
    try {
      deliver(JSON.parse(storageEvent.newValue));
    } catch {
      // Not an event this module wrote.
    }
  };

  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};

/** Test-only: forget the channel so a test can install its own. */
export const resetSessionSyncForTests = (): void => {
  channel?.close?.();
  channel = undefined;
};
